import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, Siren } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import ReflectStaffShell, {
  StaffError,
  StaffPageLoading,
  staffRequest,
} from '../../../components/reflect/staff/ReflectStaffShell';
import { StaffPanel } from '../../../components/reflect/staff/StaffControls';
import { formatTimeAgo } from '../../../utils/reflectStaff';

const AlertContent = ({ alert }) => {
  const deleted = !alert.display_name;

  if (deleted) {
    return (
      <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-4 font-bold text-white/55">
        Student data deleted
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-4">
      <p className="font-bold leading-relaxed text-white/80">
        {alert.question_text || 'Care review from a Reflect lyric draft'}
      </p>
      <p className="mt-2 whitespace-pre-wrap leading-relaxed text-white/60">
        {alert.answer_text || 'No single answer is attached to this review.'}
      </p>
    </div>
  );
};

const SeverityChip = ({ severity }) => severity === 'urgent' ? (
  <span
    className="inline-flex items-center gap-1.5 rounded-full border border-red-400/50 bg-red-500/15
      px-3 py-1 text-xs font-black uppercase tracking-wide text-red-100"
  >
    <Siren className="h-3.5 w-3.5" />
    Urgent
  </span>
) : (
  <span
    className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/45 bg-amber-400/10
      px-3 py-1 text-xs font-black uppercase tracking-wide text-amber-100"
  >
    <AlertTriangle className="h-3.5 w-3.5" />
    Review
  </span>
);

const OpenAlert = ({ alert, busy, note, noteError, onNoteChange, onHandle }) => (
  <StaffPanel className={alert.severity === 'urgent' ? 'border-red-400/35' : 'border-amber-300/25'}>
    {Boolean(alert.email_failed) && (
      <div
        className="-mx-5 -mt-5 mb-5 rounded-t-[1.6rem] border-b border-red-400/35
          bg-red-500/20 px-5 py-3 font-black text-red-100 sm:-mx-6 sm:-mt-6 sm:px-6"
      >
        Alert email failed. Use the in-app care protocol now.
      </div>
    )}
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <SeverityChip severity={alert.severity} />
        <span className="text-sm font-bold text-white/45">{formatTimeAgo(alert.created_at)}</span>
      </div>
      <span className="text-sm font-bold text-white/45">{alert.class_name || 'Deleted class'}</span>
    </div>
    <h2 className="mt-4 font-heading text-2xl font-black">
      {alert.display_name || 'Student data deleted'}
    </h2>
    <AlertContent alert={alert} />
    <form
      className="mt-5 border-t border-white/10 pt-5"
      onSubmit={(event) => {
        event.preventDefault();
        onHandle(alert);
      }}
    >
      <label htmlFor={`note-${alert.id}`} className="font-bold">Handling note</label>
      <p className="mt-1 text-sm text-white/45">Required. Keep it short and practical.</p>
      <textarea
        id={`note-${alert.id}`}
        value={note || ''}
        onChange={(event) => onNoteChange(alert.id, event.target.value)}
        maxLength={500}
        rows={3}
        className="mt-2 w-full rounded-xl border border-white/15 bg-black/30 px-4 py-3 text-white
          outline-none placeholder:text-white/30 focus:border-cyan focus:ring-2 focus:ring-cyan/25"
        placeholder="Who checked in, and what happened next?"
      />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <span className={`text-sm font-bold ${noteError ? 'text-red-200' : 'text-white/35'}`}>
          {noteError || `${(note || '').length}/500`}
        </span>
        <Button
          type="submit"
          disabled={busy}
          className="min-h-11 rounded-full bg-cyan px-5 font-black text-black hover:bg-cyan/90"
        >
          <CheckCircle2 className="mr-2 h-4 w-4" />
          Mark handled
        </Button>
      </div>
    </form>
  </StaffPanel>
);

const HandledAlert = ({ alert }) => (
  <article className="rounded-2xl border border-white/10 bg-white/[0.025] p-4 sm:p-5">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <SeverityChip severity={alert.severity} />
        <span className="font-heading text-lg font-black">
          {alert.display_name || 'Student data deleted'}
        </span>
      </div>
      <span className="text-sm text-white/40">{alert.class_name || 'Deleted class'}</span>
    </div>
    <AlertContent alert={alert} />
    <div className="mt-4 rounded-xl bg-white/[0.04] p-3 text-sm leading-relaxed text-white/55">
      <p>{alert.ack_note || 'Handled without a saved note.'}</p>
      <p className="mt-2 font-bold text-white/40">
        Handled by {alert.acked_by || 'staff'} · {formatTimeAgo(alert.acked_at)}
      </p>
    </div>
  </article>
);

const ReflectCareInbox = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [notes, setNotes] = useState({});
  const [noteErrors, setNoteErrors] = useState({});

  const loadAlerts = useCallback(async (silent = false) => {
    if (!silent) setError('');
    try {
      setAlerts(await staffRequest('/api/reflect/alerts'));
    } catch (requestError) {
      if (!silent) setError(requestError.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAlerts();
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') loadAlerts(true);
    }, 30000);
    return () => window.clearInterval(interval);
  }, [loadAlerts]);

  const openAlerts = useMemo(
    () => alerts.filter((alert) => alert.status === 'open'),
    [alerts],
  );
  const handledAlerts = useMemo(
    () => alerts.filter((alert) => alert.status !== 'open'),
    [alerts],
  );

  const changeNote = (alertId, value) => {
    setNotes((current) => ({ ...current, [alertId]: value.slice(0, 500) }));
    setNoteErrors((current) => ({ ...current, [alertId]: '' }));
  };

  const markHandled = async (alert) => {
    const note = (notes[alert.id] || '').trim();
    if (!note) {
      setNoteErrors((current) => ({ ...current, [alert.id]: 'Add a short note before marking handled.' }));
      return;
    }
    setBusyId(alert.id);
    setError('');
    try {
      await staffRequest(`/api/reflect/alerts/${alert.id}/acknowledge`, {
        method: 'POST',
        body: JSON.stringify({ note }),
      });
      setNotes((current) => ({ ...current, [alert.id]: '' }));
      await loadAlerts(true);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusyId('');
    }
  };

  return (
    <ReflectStaffShell
      title="Care inbox"
      subtitle="Open alerts stay at the top until staff records what happened next."
      alerts={alerts}
      backTo="/admin/reflect"
      backLabel="All classes"
    >
      <StaffPanel className="mb-6 border-red-400/30 bg-red-500/[0.07]">
        <div className="flex items-start gap-4">
          <Siren className="mt-0.5 h-6 w-6 shrink-0 text-red-200" />
          <div>
            <h2 className="font-heading text-lg font-black">Care-alert steps</h2>
            <p className="mt-1 leading-relaxed text-white/75">
              Follow the LTBB care-alert protocol. If a student is in danger right now, call 911.
            </p>
          </div>
        </div>
      </StaffPanel>

      <StaffError>{error}</StaffError>

      {loading ? (
        <StaffPageLoading line="Checking the care inbox..." />
      ) : (
        <div className="mt-6 space-y-7">
          <section aria-labelledby="open-alerts-title">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 id="open-alerts-title" className="font-heading text-2xl font-black">Open alerts</h2>
              <span className="rounded-full border border-white/15 px-3 py-1 text-sm font-black">
                {openAlerts.length}
              </span>
            </div>
            {openAlerts.length ? (
              <div className="space-y-4">
                {openAlerts.map((alert) => (
                  <OpenAlert
                    key={alert.id}
                    alert={alert}
                    busy={busyId === alert.id}
                    note={notes[alert.id]}
                    noteError={noteErrors[alert.id]}
                    onNoteChange={changeNote}
                    onHandle={markHandled}
                  />
                ))}
              </div>
            ) : (
              <StaffPanel className="flex min-h-56 flex-col items-center justify-center text-center">
                <img src="/pizzul/satisfied.png" alt="Pizzul" className="h-24 w-24 object-contain" />
                <h3 className="mt-3 font-heading text-xl font-black">No open alerts</h3>
                <p className="mt-2 text-white/50">The inbox refreshes every 30 seconds.</p>
              </StaffPanel>
            )}
          </section>

          {handledAlerts.length > 0 && (
            <details className="group rounded-[1.6rem] border border-white/10 bg-[#0d1011]/75">
              <summary
                className="flex min-h-16 cursor-pointer list-none items-center justify-between gap-3
                  px-5 font-heading text-xl font-black sm:px-6"
              >
                <span>Handled ({handledAlerts.length})</span>
                <ChevronDown className="h-5 w-5 text-white/45 transition group-open:rotate-180" />
              </summary>
              <div className="space-y-3 border-t border-white/10 p-4 sm:p-6">
                {handledAlerts.map((alert) => <HandledAlert key={alert.id} alert={alert} />)}
              </div>
            </details>
          )}
        </div>
      )}
    </ReflectStaffShell>
  );
};

export default ReflectCareInbox;
